import Image from 'next/image';
import router from 'next/router';

import { Box, Button, Chip, Typography, Grid, Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';

import { HeadingCpdDetail, ImageBox, DetailBox } from './style';

import EnrollButton from '@components/molecules/enrollButton';
import BreadCrumbs from 'src/components/atoms/breadcrumb';
import { FavoriteIconButton, ShareIconButton } from 'src/components/atoms/iconButton';
import NextLink from 'src/components/atoms/link';
import { CourseCpdDetailContainer } from 'src/components/templates/coursesCpdDetail/style';
import { ROUTES, EnrollmentStatus } from 'src/helpers/constants';
import { useMediaQueryDevice } from 'src/helpers/use';
import { getCourseTypeCpd, getLicenseCPDPath, getLicenseTypes, numberWithCommas } from 'src/helpers/utils';
import { IBreadcrumb, ICourse } from 'src/interfaces';
import { CPDLicenseTypeEnum } from 'src/interfaces/course';

type Props = {
  course: ICourse;
};

const IconGroup = ({ course, active }: Props & { active: boolean }) => {
  const { matchesMobile } = useMediaQueryDevice();
  return (
    <>
      <FavoriteIconButton active={active} course={course} />
      <ShareIconButton course={course} placement={matchesMobile ? 'bottom-start' : 'bottom'} />
    </>
  );
};

const Heading = ({
  course,
  onChooseTrainingClick,
  originalPrice,
  salePrice,
  cpdEnrollmentStatus,
  isLoading,
}: Props & {
  onChooseTrainingClick: () => void;
  originalPrice: number;
  salePrice: number;
  cpdEnrollmentStatus: EnrollmentStatus;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();

  const availableDaysToLearn = course.default_glances?.available_days_to_learn;

  const breadcrumbList: IBreadcrumb[] = [{ description: t('cpd_course.all_training_course'), url: ROUTES.CPD_LANDING }];
  if (course.licenseRenewal && course.licenseRenewal !== 4) {
    breadcrumbList.push({
      description: getCourseTypeCpd(course),
      url: getLicenseCPDPath(course.licenseTypes[0] as CPDLicenseTypeEnum),
    });
  }
  breadcrumbList.push({ description: course.name });
  const breadcrumbs = breadcrumbList.map((item, index) => {
    if (!item.description && !item.url) return <></>;
    if (!item.url) {
      return (
        <Typography variant="h7" key={`cpd_course_detail_${index}`} className="text-discount">
          {item.description}
        </Typography>
      );
    }
    return (
      <NextLink key={`cpd_course_detail_${index}`} href={item.url}>
        <Button className="breadcrumb-link" variant="text" sx={{ color: 'white' }}>
          {item.description}
        </Button>
      </NextLink>
    );
  });

  return (
    <HeadingCpdDetail>
      <CourseCpdDetailContainer>
        <BreadCrumbs breadcrumbs={breadcrumbs} widthLastList="60%" color="white" />
        <Grid container sx={{ m: { lg: '2.5rem 0', xs: '0 auto' }, width: { xs: '100%', md: '83.5%', lg: '100%' } }}>
          <Grid xs={12} lg={6} item>
            <ImageBox>
              <Image src={course.highlight_medium_image} alt={course.name} layout="fill" priority />
            </ImageBox>
          </Grid>
          <Grid xs={12} lg={6} item>
            <DetailBox>
              <Box>
                <Grid container alignItems="baseline">
                  <Grid xs={8} item>
                    {course.licenseTypes?.map((license, index) => (
                      <Chip
                        key={index}
                        onClick={() => router.push(getLicenseCPDPath(license))}
                        label={getLicenseTypes(license)}
                        variant="outlined"
                        sx={{
                          mr: 1,
                          color: 'white',
                          mb: '4px',
                          typography: 'h7',
                          borderColor: 'white',
                        }}
                      />
                    ))}
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      position: 'relative',
                      bottom: 1,
                      textAlign: 'right',
                      display: { xs: 'block', sm: 'block', md: 'none', lg: 'none' },
                    }}
                  >
                    <IconGroup course={course} active={!!course.wishlist} />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid md={10} lg={12} item>
                    <Typography
                      sx={{ typography: { xs: 'h4', md: 'h2' }, lineHeight: 1, mt: { md: 0.5, lg: 0 } }}
                      variant="h1"
                      color="white"
                      className="ellipsis-2"
                    >
                      {course.name}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    md={2}
                    sx={{
                      position: 'relative',
                      bottom: 2,
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'none', md: 'block', lg: 'none' },
                    }}
                  >
                    <IconGroup course={course} active={!!course.wishlist} />
                  </Grid>
                </Grid>
                {!originalPrice || originalPrice === salePrice || originalPrice < salePrice ? (
                  <Typography
                    component="span"
                    sx={{ fontSize: { md: '48px', xs: '30px' }, fontWeight: 'bold', lineHeight: 1 }}
                    color="white"
                  >
                    {salePrice
                      ? `${numberWithCommas(salePrice)} ${t('home.course_card_baht')}`
                      : t('home.course_card_free')}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      component="span"
                      sx={{ fontSize: { md: '48px', xs: '30px' }, fontWeight: 'bold', lineHeight: 1 }}
                      color="white"
                    >
                      {salePrice
                        ? `${numberWithCommas(salePrice)} ${t('home.course_card_baht')}`
                        : t('home.course_card_free')}
                    </Typography>
                    <Typography
                      className="strike-center text-discount"
                      sx={{
                        mx: { md: '1.5rem', xs: '1rem' },
                        typography: { md: 'h4', xs: 'h7' },
                        position: 'relative',
                        bottom: { md: '4px', xs: '1px' },
                      }}
                      component="span"
                    >
                      {numberWithCommas(originalPrice)} {t('home.course_card_baht')}
                    </Typography>
                  </>
                )}
              </Box>
              <Stack
                sx={{
                  height: '100%',
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'end',
                }}
              >
                <Grid className="train" container>
                  <Grid md={12} lg={9} width="100%" item>
                    <EnrollButton
                      type={cpdEnrollmentStatus}
                      onClick={onChooseTrainingClick}
                      isLoading={isLoading}
                      availableDaysToLearn={availableDaysToLearn}
                      isCpd
                    />
                  </Grid>
                  <Grid
                    item
                    md={3}
                    sx={{
                      m: '0.5rem 0',
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
                    }}
                  >
                    <IconGroup course={course} active={!!course.wishlist} />
                  </Grid>
                </Grid>
              </Stack>
            </DetailBox>
          </Grid>
        </Grid>
      </CourseCpdDetailContainer>
    </HeadingCpdDetail>
  );
};

export default Heading;
